import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { STARTING_TEMPLATES, type StartingTemplate } from "@brainlog/shared";
import { errorMessage, post } from "@/lib/api";
import { qk } from "@/lib/queries";
import { useUi, openView } from "@/lib/store";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

export function TemplatePreview({ templateKey }: { templateKey: string }) {
  const t = STARTING_TEMPLATES.find((x) => x.key === templateKey);
  if (!t) return null;
  if (!t.items.length) return <p className="text-sm text-muted-foreground">No folders or tasks are created.</p>;
  return (
    <ul className="space-y-0.5 text-sm">
      {t.items.map((i) => (
        <li key={i.key}>
          <span className="text-muted-foreground">{i.type === "folder" ? "Folder" : i.type === "task" ? (i.taskKind === "milestone" ? "Milestone (no date)" : "Deliverable (no date)") : "Manuscript record"}:</span>{" "}
          {i.label}
        </li>
      ))}
    </ul>
  );
}

export function CreateProjectDialog({ open, onOpenChange }: { open: boolean; onOpenChange(o: boolean): void }) {
  const qc = useQueryClient();
  const setWorkspace = useUi((s) => s.setWorkspace);
  const [name, setName] = useState("");
  const [tpl, setTpl] = useState<StartingTemplate>("blank");
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto text-foreground sm:max-w-2xl">
        <form
          className="space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!name.trim()) return;
            setBusy(true);
            try {
              const { workspace } = await post<{ workspace: { id: string; name: string } }>("/api/workspaces", { kind: "project", name: name.trim(), startingTemplate: tpl });
              await qc.invalidateQueries({ queryKey: qk.workspaces });
              setWorkspace(workspace.id);
              openView("overview", workspace.id, "Overview");
              onOpenChange(false);
              setName("");
            } catch (err) {
              toast.error(errorMessage(err));
            } finally {
              setBusy(false);
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>New research project</DialogTitle>
            <DialogDescription>
              A starting template only adds folders and undated tasks you can rename or delete. It never sets permissions and no field is required.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="proj-name">Project name</Label>
            <Input id="proj-name" value={name} onChange={(e) => setName(e.target.value)} autoFocus maxLength={200} required />
          </div>
          <fieldset>
            <legend className="mb-2 text-sm font-medium">Starting template</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {STARTING_TEMPLATES.map((t) => (
                <label key={t.key} className={cn("cursor-pointer rounded-md border p-3 text-sm hover:bg-accent", tpl === t.key && "border-primary bg-accent")}>
                  <span className="flex items-center gap-2 font-medium">
                    <input type="radio" name="start-tpl" checked={tpl === t.key} onChange={() => setTpl(t.key)} /> {t.name}
                  </span>
                  <span className="mt-1 block text-xs text-muted-foreground">{t.summary}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <div className="rounded-md border bg-muted/40 p-3">
            <p className="mb-1 text-sm font-medium">This will create</p>
            <TemplatePreview templateKey={tpl} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy || !name.trim()}>
              {busy ? "Creating…" : "Create project"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** A notebook holds notes, folders, files and tasks — private until you share it. */
export function CreateNotebookDialog({ open, onOpenChange }: { open: boolean; onOpenChange(o: boolean): void }) {
  const qc = useQueryClient();
  const setWorkspace = useUi((s) => s.setWorkspace);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="text-foreground sm:max-w-md">
        <form
          className="space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!name.trim()) return;
            setBusy(true);
            try {
              const { workspace } = await post<{ workspace: { id: string; name: string } }>("/api/workspaces", { kind: "notebook", name: name.trim() });
              await qc.invalidateQueries({ queryKey: qk.workspaces });
              setWorkspace(workspace.id);
              onOpenChange(false);
              setName("");
            } catch (err) {
              toast.error(errorMessage(err));
            } finally {
              setBusy(false);
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>New notebook</DialogTitle>
            <DialogDescription>A notebook keeps notes, folders, files and tasks. It is private to you until you share it from its settings.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="nb-name">Notebook name</Label>
            <Input id="nb-name" value={name} onChange={(e) => setName(e.target.value)} autoFocus maxLength={200} required placeholder="e.g. Lab notebook" />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy || !name.trim()}>
              {busy ? "Creating…" : "Create notebook"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
